import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buildQuery, useApi } from './client'
import type { ListResponse, Project } from './types'

const ROOT = '/projects'
const KEY = ['projects'] as const

export function useProjects(filter: { organizationId?: string | null } = {}) {
  const api = useApi()
  const orgId = filter.organizationId ?? undefined
  return useQuery({
    queryKey: [...KEY, { organizationId: orgId ?? null }],
    queryFn: () =>
      api<ListResponse<Project>>(`${ROOT}${buildQuery({ organization_id: orgId })}`).then(
        (r) => r.items,
      ),
  })
}

export type CreateProjectInput = {
  name: string
  organizationId?: string | null
  parentId?: string | null
  code?: string | null
}

export function useCreateProject() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateProjectInput) =>
      api<Project>(ROOT, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export type UpdateProjectInput = {
  id: string
  patch: Partial<{
    name: string
    organizationId: string | null
    parentId: string | null
    code: string | null
    isArchived: boolean
  }>
}

export function useUpdateProject() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: UpdateProjectInput) =>
      api<Project>(`${ROOT}/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteProject() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`${ROOT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
